import { formatTooltipValue } from '@/helpers/formatters';
import theme from '@ui/configs/vuetify';
import { customTooltip } from '@/components/insights/Chart/oldChartTooltip';

const {
  textDarkest, white, grey400, sourceReferral,
} = theme.theme.themes.light.colors;

export const backgroundTextColorExceptions = {
  [grey400]: textDarkest,
  [sourceReferral]: textDarkest,
};

// Common
export const dataLabelsConfig = {
  color: function colorCallback(context) {
    const max = Math.max(...context.dataset.data.filter((value) => value));
    const value = context.dataset.data[context.dataIndex];
    const percentOfSpace = (value / max) * 100;
    const backgroundColor = context.dataset.backgroundColor[context.dataIndex];
    const barTextColor = backgroundTextColorExceptions[backgroundColor] || white;

    return percentOfSpace < 20 ? textDarkest : barTextColor;
  },
  anchor: 'end',
  align: function alignCallback(context) {
    const max = Math.max(...context.dataset.data.filter((value) => value));
    const value = context.dataset.data[context.dataIndex];
    const percentOfSpace = (value / max) * 100;

    if (Number.isNaN(percentOfSpace)) return 'end';

    return percentOfSpace < 20 ? 'end' : 'start';
  },
  formatter: function formatterCallback(value) {
    if (Number.isNaN(value)) return '';
    return formatTooltipValue(value, 'percent');
  },
};

export const flippedAxisTooltipPluginConfig = (chartData, widgetId) => ({
  mode: 'index',
  enabled: false,
  external: customTooltip(
    'basicChart',
    {
      chartData: {
        originalLabels: chartData.value.labels,
        datasets: chartData.value.datasets,
      },
      isFlippedAxis: true,
      labelFormat: 'percent',
    },
    widgetId,
  ),
  bodyFont: {
    size: 16,
    family: 'Inter',
  },
});

export const xScaleWithPercentFormatConfig = {
  title: {
    display: false,
  },
  beginAtZero: true,
  border: {
    display: false,
    dash: [2, 3],
  },
  grid: {
    display: true,
    color: grey400.value,
    tickLength: 0,
  },
  ticks: {
    padding: 24,
    callback: function xTickCallback(value) {
      return `${value}%`;
    },
  },
};

export const basicYScaleConfig = {
  border: {
    display: false,
  },
  grid: {
    display: false,
  },
  offset: true,
};

export const hiddenYScaleConfig = {
  display: false,
  border: {
    display: false,
  },
  grid: {
    display: false,
  },
  offset: true,
};

export const basicChartTooltipPluginConfig = (chartData, widgetId) => ({
  enabled: false,
  external: customTooltip(
    'basicChart',
    {
      chartData: {
        originalLabels: chartData.value.labels,
        datasets: chartData.value.datasets,
      },
      isFlippedAxis: true,
      labelFormat: 'percent',
    },
    widgetId,
  ),
});

// Chart specific
export const donutChartTooltipPluginConfig = (chartData, widgetId) => ({
  mode: 'index',
  enabled: false,
  external: customTooltip(
    'donut',
    {
      isCompare: false,
      chartData: {
        originalLabels: chartData.value.labels,
        datasets: chartData.value.datasets,
        values: chartData.value,
      },
      labelFormat: 'percent',
    },
    widgetId,
  ),
  bodyFont: {
    size: 16,
    family: 'Inter',
  },
});

export const donutChartLegendPluginConfig = (colors) => ({
  position: 'right',
  align: 'start',
  labels: {
    font: {
      size: 12,
      weight: 400,
      lineHeight: 16,
      family: 'Inter',
    },
    usePointStyle: true,
    padding: 22,
    generateLabels(chart) {
      const dataset = chart.data.datasets[0] || {};

      return chart.data.labels
        .filter((_, index) => typeof dataset?.data?.[index] === 'number')
        .map((label, index) => {
          const value = dataset?.data?.[index];
          const formattedValue = formatTooltipValue(value, 'percent') || '';

          return {
            boxWidth: 12,
            boxHeight: 12,
            borderRadius: 4,
            pointStyle: 'rectRounded',
            text: `${label} ${formattedValue}`,
            fillStyle: colors[index],
            strokeStyle: colors[index],
          };
        });
    },
  },
});

export const donutChartScalesOptions = {
  y: {
    title: {
      display: false,
    },
    beginAtZero: true,
    border: {
      display: false,
    },
    grid: {
      display: false,
    },
    ticks: {
      display: false,
    },
  },
  x: {
    ticks: {
      display: false,
    },
    border: {
      display: false,
    },
    grid: {
      display: false,
    },
  },
};

export const benchmarkPluginNumericOffset = {
  leftOffset: 26,
  rightOffset: 30,
};

export const benchmarkPluginPercentOffset = {
  leftOffset: 33,
  rightOffset: 40,
};

// Report specific
export const executiveInsightsPluginsConfig = (chartData, widgetId) => ({
  legend: {
    display: true,
    position: 'right',
    align: 'start',
    maxWidth: 200,
    onClick: () => {},
    labels: {
      boxWidth: 12,
      boxHeight: 12,
      generateLabels: (chart) => chart.data.datasets.map((dataset) => ({
        text: dataset.label,
        fillStyle: dataset.backgroundColor,
        strokeStyle: dataset.backgroundColor,
        borderRadius: 4,
      })),
    },
  },
  tooltip: {
    mode: 'index',
    enabled: false,
    external: customTooltip('basicChart', {
      chartData: {
        originalLabels: chartData.value.labels,
        datasets: chartData.value.datasets,
      },
      labelFormat: 'percent',
    }, widgetId),
    bodyFont: {
      size: 16,
      family: 'Inter',
    },
  },
});

export const executiveInsightsYAxisOptions = (maxValue) => ({
  offset: true,
  title: {
    display: false,
  },
  beginAtZero: true,
  border: {
    display: false,
    dash: [2, 3],
  },
  grid: {
    display: true,
    color: grey400.value,
    tickLength: 0,
  },
  max: maxValue,
  suggestedMax: maxValue,
  ticks: {
    padding: 10,
    autoSkip: false,
    stepSize: 25,
    callback(value) {
      return `${value}%`;
    },
  },
});

export const executiveInsightsXScaleOptions = {
  border: {
    display: false,
  },
  grid: {
    display: false,
  },
  ticks: {
    font: {
      weight: '500',
    },
  },
};

export const hiringReportXScaleOptions = (max) => ({
  max,
  title: {
    display: false,
  },
  beginAtZero: true,
  border: {
    display: false,
    dash: [2, 3],
  },
  grid: {
    display: true,
    color: grey400.value,
    tickLength: 0,
  },
  ticks: {
    padding: 24,
    maxRotation: 0,
    callback: function xTickCallback(value) {
      return `${value}%`;
    },
  },
});
